<template>
  <div class="min-h-screen">
    <AppNavbar />
    
    <main class="page-gutter pt-32 pb-20">
      <article class="max-w-4xl mx-auto card">
        <header class="mb-8 pb-6 border-b border-border">
          <h1 class="text-4xl sm:text-5xl font-extrabold mb-4 flex items-center gap-3">
            <svg class="w-10 h-10 text-primary-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M15,18V16H6V18H15M18,14V12H6V14H18Z"/>
            </svg>
            <span class="gradient-text">Terms of Service</span>
          </h1>
          <p class="text-muted text-sm">Last updated: October 11, 2025</p>
        </header>

        <section class="space-y-6 text-muted prose prose-invert max-w-none">
          <p>
            Welcome to App to Link! These Terms of Service ("Terms") govern your use of our mobile application and any related services (collectively, the "Service"). Please read them carefully.
          </p>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">2. The Service</h2>
            <p>
              App to Link allows you to connect your Instagram posts with custom redirect URLs. The Service works by:
            </p>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>Authenticating with your Instagram account to access your posts.</li>
              <li>Allowing you to associate a redirect URL with each post.</li>
              <li>Providing a unique link that, when shared, directs users to your specified URL.</li>
            </ul>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">3. Your Account and Responsibilities</h2>
            <h3 class="text-xl font-semibold text-text mb-3">Authentication</h3>
            <p>
              You must authenticate using your Instagram account to use our Service. We use Instagram's official API for authentication and do not store your Instagram password.
            </p>

            <h3 class="text-xl font-semibold text-text mb-3 mt-6">Content</h3>
            <p>
              You are solely responsible for the content you post and the URLs you link to. You agree not to link to any content that is illegal, harmful, fraudulent, or infringes on any third party's rights.
            </p>

            <h3 class="text-xl font-semibold text-text mb-3 mt-6">Data Usage</h3>
            <p>
              We access your Instagram media and basic profile information as authorized by you during the authentication process. Our use of this data is governed by our Privacy Policy.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">4. User Conduct</h2>
            <p>You agree not to misuse the Service. This includes, but is not limited to:</p>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>Using the Service for any illegal or unauthorized purpose.</li>
              <li>Linking to spam, malware, or phishing sites.</li>
              <li>Attempting to interfere with the Service's infrastructure.</li>
              <li>Violating Instagram's Terms of Use.</li>
            </ul>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">5. Intellectual Property</h2>
            <h3 class="text-xl font-semibold text-text mb-3">Our Service</h3>
            <p>
              We own all rights, title, and interest in and to the App to Link Service, including its design, software, and branding.
            </p>

            <h3 class="text-xl font-semibold text-text mb-3 mt-6">Your Content</h3>
            <p>
              You retain ownership of your content (your posts and the content at your redirect URLs). By using the Service, you grant us a limited license to use your post media and metadata for the purpose of providing and improving the Service.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">6. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Service at any time, without notice, for any reason, including but not limited to a breach of these Terms. You can stop using the Service at any time by uninstalling the application.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">7. Disclaimers and Limitation of Liability</h2>
            <h3 class="text-xl font-semibold text-text mb-3">"As Is"</h3>
            <p>
              The Service is provided "as is" without any warranties of any kind. We do not guarantee that the Service will always be safe, secure, or error-free.
            </p>

            <h3 class="text-xl font-semibold text-text mb-3 mt-6">Liability</h3>
            <p>
              To the fullest extent permitted by law, App to Link shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">8. Changes to These Terms</h2>
            <p>
              We may modify these Terms from time to time. We will notify you of any changes by posting the new Terms within the app. Your continued use of the Service after any changes constitutes your acceptance of the new Terms.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">9. Governing Law</h2>
            <p>
              These Terms shall be governed by the laws of the United Arab Emirates, without regard to its conflict of law provisions.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">10. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us:</p>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>By email: <a :href="infoMailto" class="text-primary-400 hover:text-primary-300 underline">{{ infoEmail }}</a></li>
              <li>By visiting this page on our website: <a href="https://apptolink.com" rel="external nofollow noopener" target="_blank" class="text-primary-400 hover:text-primary-300 underline">https://apptolink.com</a></li>
            </ul>
          </div>
        </section>
      </article>
    </main>

    <!-- Footer -->
    <footer class="page-gutter py-8 border-t border-border">
      <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p class="text-muted text-sm">&copy; 2024 App to Link. All rights reserved.</p>
        <nav class="flex items-center gap-6 text-sm text-muted">
          <router-link to="/" class="hover:text-text transition">Home</router-link>
          <router-link to="/privacy-policy" class="hover:text-text transition">Privacy Policy</router-link>
          <router-link to="/terms-of-service" class="hover:text-text transition">Terms of Service</router-link>
          <router-link to="/data-deletion" class="hover:text-text transition">Data Deletion</router-link>
          <a :href="infoMailto" class="hover:text-text transition">Contact</a>
        </nav>
      </div>
    </footer>
  </div>
</template>

<script>
import AppNavbar from './AppNavbar.vue';
import emailObfuscation from '@/mixins/emailObfuscation';

export default {
  name: 'TermsOfService',
  components: { AppNavbar },
  mixins: [emailObfuscation]
}
</script>

